import React from 'react';
import { motion } from 'framer-motion';
import { FileText, TrendingDown, AlertCircle, CheckCircle } from 'lucide-react';

const RewriteDisplay = ({ rewriteData, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="rewrite-loading">
        <div className="loading-animation">
          <div className="spinner"></div>
          <p>Rewriting regulation in plain language...</p>
          <p className="loading-subtitle">Removing redundancies and unauthorized provisions</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rewrite-error">
        <AlertCircle size={48} />
        <h3>Rewrite Generation Failed</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!rewriteData) {
    return (
      <div className="rewrite-empty">
        <FileText size={48} />
        <h3>No Rewrite Generated</h3>
        <p>Click "Generate Plain Language Rewrite" to simplify this regulation</p>
      </div>
    );
  }

  // Calculate stats from the actual text
  const originalWords = rewriteData.original_text ? rewriteData.original_text.split(/\s+/).length : 0;
  const rewrittenWords = rewriteData.rewritten_text ? rewriteData.rewritten_text.split(/\s+/).length : 0;
  const wordsRemoved = originalWords - rewrittenWords;
  const percentReduction = originalWords > 0 ? Math.round((wordsRemoved / originalWords) * 100) : 0;
  
  const reductionClass = percentReduction >= 30 
    ? 'reduction-high' 
    : percentReduction >= 15 
    ? 'reduction-medium' 
    : 'reduction-low';

  return (
    <motion.div 
      className="rewrite-display"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Stats Header */}
      <div className="rewrite-stats">
        <div className="stat-card">
          <TrendingDown className={`stat-icon ${reductionClass}`} />
          <div className="stat-content">
            <span className="stat-value">{percentReduction}%</span>
            <span className="stat-label">Reduction</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-value">{originalWords}</span>
            <span className="stat-label">Original Words</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-value">{rewrittenWords}</span>
            <span className="stat-label">Rewritten Words</span>
          </div>
        </div>
        <div className="stat-card">
          <CheckCircle className="stat-icon success" />
          <div className="stat-content">
            <span className="stat-value">{wordsRemoved}</span>
            <span className="stat-label">Words Removed</span>
          </div>
        </div>
      </div>

      {/* Rewritten Text */}
      <div className="rewrite-content">
        <h3 className="rewrite-title" style={{ marginBottom: '12px' }}>Plain Language Version</h3>
        <div className="rewritten-text" style={{ 
          backgroundColor: '#f9fafb',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          {rewriteData.rewritten_text && rewriteData.rewritten_text.split('\n').map((paragraph, index) => {
            if (paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-')) {
              return (
                <li key={index} className="rewrite-list-item">
                  {paragraph.replace(/^[•-]\s*/, '')}
                </li>
              );
            }
            return paragraph.trim() && (
              <p key={index} className="rewrite-paragraph">
                {paragraph}
              </p>
            );
          })}
        </div>
      </div>

      {/* Rewrite Analysis Section */}
      <div className="rewrite-content" style={{ marginTop: '24px' }}>
        <h3 className="rewrite-title" style={{ marginBottom: '12px' }}>Rewrite Analysis</h3>
        <div className="rewritten-text" style={{ 
          backgroundColor: '#f9fafb',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          minHeight: '100px'
        }}>
          {rewriteData.rewrite_analysis ? (
            rewriteData.rewrite_analysis.split('\n').map((paragraph, index) => {
              if (paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-')) {
                return (
                  <li key={index} className="rewrite-list-item">
                    {paragraph.replace(/^[•-]\s*/, '')}
                  </li>
                );
              }
              return paragraph.trim() && (
                <p key={index} className="rewrite-paragraph">
                  {paragraph}
                </p>
              );
            })
          ) : (
            <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No analysis available</p>
          )}
        </div>
      </div>

    </motion.div>
  );
};

export default RewriteDisplay;